import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "../hooks/useInView";
import { PhoneMockup } from "../components/PhoneMockup";
import { Accordion, type AccordionItem } from "../components/Accordion";

// ============================================================
// PROGRAM VARIABLES - Update these per cycle
// ============================================================
const PROGRAM = {
  // Partner info
  partnerName: "University of St. Thomas",
  partnerShortName: "St. Thomas",
  sponsorName: "US Bank",
  programCode: "[PROGRAM CODE]",

  // Prize info
  grandPrizeAmount: "[GRAND PRIZE AMOUNT]",
  grandPrizeDescription: "[GRAND PRIZE DESCRIPTION]",
  weeklyPrizeAmount: "[WEEKLY PRIZE AMOUNT]",
  weeklyPrizeDescription: "[WEEKLY PRIZE DESCRIPTION]",

  // Dates
  programStart: "[START DATE]",
  programEnd: "[END DATE]",
  drawingDate: "[DRAWING DATE]",

  // Eligibility
  eligibility:
    "[ELIGIBILITY REQUIREMENTS - e.g., Must be a currently enrolled student at the University of St. Thomas.]",
  minAge: "18",

  // Links
  officialRulesUrl: "#",
  contactEmail: "support@dough-app.ai",

  // Distribution
  distributionMethod: "[PROGRAM DISTRIBUTION METHOD - e.g., your university email]",
  contactPlaceholder: "[CONTACT PLACEHOLDER - e.g., your program coordinator]",

  // App links
  appStoreUrl:
    "https://apps.apple.com/us/app/dough-financial-wellness/id6757439224",
  playStoreUrl:
    "https://play.google.com/store/apps/details?id=com.betteryou.BetterFinance&hl=en_US",
} as const;

// ============================================================
// FAQ Data
// ============================================================
const ustFaqs: AccordionItem[] = [
  {
    id: "faq-how-to-join",
    question: "How do I join the program?",
    answer:
      "Download the Dough app, create an account, and enter your program code in the Profile tab. Once your code is accepted, you\u2019re enrolled and immediately eligible for competitions and prize drawings.",
  },
  {
    id: "faq-find-program-code",
    question: "Where do I find my program code?",
    answer: `Your program code was included in the email or materials you received from ${PROGRAM.distributionMethod}. If you can\u2019t find it, contact ${PROGRAM.contactPlaceholder}.`,
  },
  {
    id: "faq-earn-entries",
    question: "What do I need to do to earn entries?",
    answer:
      "Just use the app. Completing lessons, playing the Sim, and taking daily quizzes all earn you raffle tickets automatically. There\u2019s no separate entry process  - every eligible activity counts.",
  },
  {
    id: "faq-grand-prize-drawing",
    question: "How does the grand prize drawing work?",
    answer: `Every raffle ticket you earn is an entry in the grand prize drawing. The drawing takes place on ${PROGRAM.drawingDate} and the prize is ${PROGRAM.grandPrizeAmount}. The more you use the app before the deadline, the more entries you accumulate.`,
  },
  {
    id: "faq-weekly-competition",
    question: "How does the weekly Sim competition work?",
    answer: `Each week, a new Sim competition launches for all enrolled students in your program. Everyone plays the same 7-day simulation, aiming for a specific goal  - highest net worth, best balanced stats, maximum happiness, and more. Results are ranked on a leaderboard. The top finisher(s) each week win ${PROGRAM.weeklyPrizeAmount}.`,
  },
  {
    id: "faq-program-dates",
    question: "When does the program start and end?",
    answer: `The program runs from ${PROGRAM.programStart} to ${PROGRAM.programEnd}. The grand prize drawing is on ${PROGRAM.drawingDate}. Weekly competitions run every week during the program period.`,
  },
  {
    id: "faq-eligibility",
    question: "Who is eligible to participate?",
    answer: (
      <p>
        {PROGRAM.eligibility} See the{" "}
        <a
          href={PROGRAM.officialRulesUrl}
          className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
        >
          Official Rules
        </a>{" "}
        for complete eligibility details.
      </p>
    ),
  },
  {
    id: "faq-existing-account",
    question:
      "I already have a Dough account  - do I need to sign up again?",
    answer:
      "No. If you already have a Dough account, just open the app, go to your Profile, and enter your program code to enroll. Your existing account and progress carry over.",
  },
  {
    id: "faq-remote-participation",
    question: "Can I participate if I\u2019m studying remotely or off-campus?",
    answer:
      "Yes. Dough is entirely mobile  - you can participate from anywhere as long as you have the app and an active internet connection.",
  },
  {
    id: "faq-official-rules",
    question: "Where can I find the Official Rules?",
    answer: (
      <p>
        The full{" "}
        <a
          href={PROGRAM.officialRulesUrl}
          className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
        >
          Official Rules are available here
        </a>
        . They cover eligibility, how tickets and competition results are
        determined, prize details, and drawing procedures.
      </p>
    ),
  },
  {
    id: "faq-data-safety",
    question: "Is my personal or financial data safe?",
    answer: (
      <p>
        Yes. Dough does not connect to your bank accounts or store sensitive
        financial credentials. See our{" "}
        <a
          href="/privacy"
          className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
        >
          Privacy Policy
        </a>{" "}
        for full details.
      </p>
    ),
  },
  {
    id: "faq-contact",
    question:
      "I have a question not answered here  - who do I contact?",
    answer: (
      <p>
        Reach out to{" "}
        <a
          href={`mailto:${PROGRAM.contactEmail}`}
          className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
        >
          {PROGRAM.contactEmail}
        </a>
        . For questions about prize eligibility or official rules, reference the{" "}
        <a
          href={PROGRAM.officialRulesUrl}
          className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
        >
          Official Rules
        </a>{" "}
        document.
      </p>
    ),
  },
];

// ============================================================
// How to Participate Steps
// ============================================================
const steps = [
  {
    number: "01",
    title: "Download",
    description: "Get the Dough app free on the App Store or Google Play.",
    color: "text-brand-teal",
    bg: "bg-brand-teal",
    border: "border-brand-teal/30",
  },
  {
    number: "02",
    title: "Enter Code",
    description: `Open the app, go to your Profile, and enter your program code: ${PROGRAM.programCode}.`,
    color: "text-brand-indigo",
    bg: "bg-brand-indigo",
    border: "border-brand-indigo/30",
  },
  {
    number: "03",
    title: "Learn & Play",
    description:
      "Complete lessons, play the Sim, and take daily quizzes to earn raffle tickets.",
    color: "text-brand-purple",
    bg: "bg-brand-purple",
    border: "border-brand-purple/30",
  },
  {
    number: "04",
    title: "Win",
    description:
      "Compete weekly and earn entries into the grand prize drawing.",
    color: "text-brand-gold",
    bg: "bg-brand-gold",
    border: "border-brand-gold/30",
  },
];

// ============================================================
// Program Benefits
// ============================================================
const benefits = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
      </svg>
    ),
    title: `Win ${PROGRAM.grandPrizeAmount}`,
    description: `Earn raffle tickets for a chance at the ${PROGRAM.grandPrizeDescription}. Plus weekly prizes for top Sim performers.`,
    color: "text-brand-gold",
    border: "border-brand-gold/30",
    bg: "bg-brand-gold/15",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
      </svg>
    ),
    title: "Build Real Skills",
    description:
      "Short lessons, an AI coach, and goal-setting tools that teach you to manage money  - not just read about it.",
    color: "text-brand-teal",
    border: "border-brand-teal/30",
    bg: "bg-brand-teal/15",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
      </svg>
    ),
    title: "Compete Weekly",
    description:
      "Go head-to-head with other St. Thomas students in weekly Sim competitions with real prizes.",
    color: "text-brand-indigo",
    border: "border-brand-indigo/30",
    bg: "bg-brand-indigo/15",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
    title: "100% Free",
    description:
      "Dough is completely free to download and use. No subscriptions, no hidden fees.",
    color: "text-brand-purple",
    border: "border-brand-purple/30",
    bg: "bg-brand-purple/15",
  },
];

// ============================================================
// Partner Navbar
// ============================================================
function PartnerNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#program", label: "Program" },
    { href: "#prizes", label: "Prizes" },
    { href: "#how-to-participate", label: "How to Join" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-bg-deep/80 border-b border-border-primary/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/ust" className="flex items-center gap-3">
          <img src="/images/logo-white.svg" alt="Dough" className="h-7" />
          <span className="text-text-tertiary/40 text-sm">&times;</span>
          <img
            src="/images/partners/st-thomas.png"
            alt={PROGRAM.partnerName}
            className="h-20 object-contain brightness-0 invert opacity-70"
          />
          <span className="text-text-tertiary/40 text-sm">&times;</span>
          <img
            src="/images/partners/us-bank.png"
            alt={PROGRAM.sponsorName}
            className="h-10 object-contain brightness-0 invert opacity-70"
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-text-secondary hover:text-white transition-colors text-sm font-medium"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#download"
            className="bg-brand-teal hover:bg-brand-teal/90 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors"
          >
            Download
          </a>
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            {mobileOpen ? (
              <path d="M6 6l12 12M6 18L18 6" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden backdrop-blur-xl bg-bg-deep/95 border-t border-border-primary/50 px-4 pb-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block py-3 text-text-secondary hover:text-white transition-colors text-sm font-medium"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#download"
            className="block mt-2 text-center bg-brand-teal hover:bg-brand-teal/90 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors"
            onClick={() => setMobileOpen(false)}
          >
            Download
          </a>
        </div>
      )}
    </nav>
  );
}

// ============================================================
// Partner Footer
// ============================================================
function PartnerFooter() {
  return (
    <footer className="bg-bg-deep border-t border-border-primary/50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* App store badges */}
        <div className="flex flex-wrap items-center gap-4 justify-center mb-8">
          <a
            href={PROGRAM.appStoreUrl}
            className="transition-transform hover:scale-105"
          >
            <img
              src="/images/appstore-badge.svg"
              alt="Download on the App Store"
              className="h-[44px]"
            />
          </a>
          <a
            href={PROGRAM.playStoreUrl}
            className="transition-transform hover:scale-105"
          >
            <img
              src="/images/googleplay-badge.svg"
              alt="Get it on Google Play"
              className="h-[44px]"
            />
          </a>
        </div>

        {/* Links */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
          <a
            href={PROGRAM.officialRulesUrl}
            className="text-text-tertiary hover:text-white text-sm transition-colors"
          >
            Official Rules
          </a>
          <Link
            to="/privacy"
            className="text-text-tertiary hover:text-white text-sm transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="text-text-tertiary hover:text-white text-sm transition-colors"
          >
            Terms of Service
          </Link>
          <a
            href={`mailto:${PROGRAM.contactEmail}`}
            className="text-text-tertiary hover:text-white text-sm transition-colors"
          >
            Contact
          </a>
        </div>

        {/* Co-brand logos */}
        <div className="flex items-center justify-center gap-8 mb-6">
          <img
            src="/images/logo-white.svg"
            alt="Dough"
            className="h-6 opacity-50"
          />
          <span className="text-text-tertiary/30">|</span>
          <img
            src="/images/partners/st-thomas.png"
            alt={PROGRAM.partnerName}
            className="h-8 object-contain brightness-0 invert opacity-30"
          />
          <span className="text-text-tertiary/30">|</span>
          <img
            src="/images/partners/us-bank.png"
            alt={PROGRAM.sponsorName}
            className="h-6 object-contain brightness-0 invert opacity-30"
          />
        </div>

        {/* Disclaimer */}
        <p className="text-text-tertiary/50 text-xs text-center max-w-2xl mx-auto mb-4">
          [SPONSOR DISCLAIMER / CO-BRAND LEGAL LINE PLACEHOLDER - to be
          confirmed with {PROGRAM.sponsorName}]
        </p>

        <p className="text-text-tertiary/40 text-xs text-center">
          &copy; {new Date().getFullYear()} BetterTime Co. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

// ============================================================
// Page Component
// ============================================================
export function USTPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-bg-deep">
      <PartnerNavbar />

      {/* ─── Section 1: Hero ─── */}
      <HeroSection />

      {/* ─── Section 2: What's the Program? ─── */}
      <ProgramSection />

      {/* ─── Section 3: The Prizes ─── */}
      <PrizesSection />

      {/* ─── Section 4: How to Participate ─── */}
      <ParticipateSection />

      {/* ─── Section 5: Eligibility & Deadlines ─── */}
      <EligibilitySection />

      {/* ─── Section 6: About Dough ─── */}
      <AboutSection />

      {/* ─── Section 7: FAQ ─── */}
      <FAQSection />

      {/* ─── Section 8: Download CTA ─── */}
      <DownloadSection />

      <PartnerFooter />
    </div>
  );
}

// ============================================================
// Section Components
// ============================================================

function HeroSection() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative pt-28 pb-20 bg-bg-deep overflow-hidden">
      {/* Gradient blobs */}
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-brand-indigo/10 rounded-full blur-3xl animate-pulse-glow" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Copy */}
          <div className="flex-1 text-center lg:text-left">
            <h1
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 hero-enter ${
                visible ? "visible" : ""
              }`}
              style={{ transitionDelay: "100ms" }}
            >
              {PROGRAM.partnerShortName} Students:{" "}
              <span className="text-brand-teal">Learn. Play. Win.</span>
            </h1>

            <p
              className={`text-text-secondary text-lg sm:text-xl max-w-xl mx-auto lg:mx-0 mb-3 hero-enter ${
                visible ? "visible" : ""
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              A free financial wellness program with real prizes -
              powered by Dough and sponsored by {PROGRAM.sponsorName}.
            </p>

            <p
              className={`text-text-tertiary text-sm mb-8 hero-enter ${
                visible ? "visible" : ""
              }`}
              style={{ transitionDelay: "250ms" }}
            >
              Powered by {PROGRAM.sponsorName}
            </p>

            <div
              className={`flex flex-wrap items-center gap-4 justify-center lg:justify-start hero-enter ${
                visible ? "visible" : ""
              }`}
              style={{ transitionDelay: "300ms" }}
            >
              <a
                href={PROGRAM.appStoreUrl}
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/appstore-badge.svg"
                  alt="Download on the App Store"
                  className="h-[52px]"
                />
              </a>
              <a
                href={PROGRAM.playStoreUrl}
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/googleplay-badge.svg"
                  alt="Get it on Google Play"
                  className="h-[52px]"
                />
              </a>
            </div>
          </div>

          {/* Phone mockup */}
          <div
            className={`flex-shrink-0 hero-enter ${
              visible ? "visible" : ""
            }`}
            style={{ transitionDelay: "400ms" }}
          >
            <PhoneMockup
              src="/images/screenshots/dashboard.png"
              alt="Dough app dashboard"
              width={280}
              className="animate-float"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgramSection() {
  const [ref, inView] = useInView();

  return (
    <section id="program" className="relative py-24 bg-bg-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-brand-gold font-semibold tracking-wide uppercase text-sm mb-3">
            The Program
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            What you get as a{" "}
            <span className="text-brand-gold">
              {PROGRAM.partnerShortName} student
            </span>
          </h2>
          <p className="mt-4 text-text-secondary text-lg max-w-2xl mx-auto">
            Full access to Dough plus eligibility for competitions and prize
            drawings - exclusively for your program.
          </p>
        </div>

        <div
          ref={ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {benefits.map((benefit, i) => (
            <div
              key={benefit.title}
              className={`bg-bg-secondary/60 backdrop-blur-sm border border-border-primary rounded-2xl p-6 hover:${benefit.border} transition-all duration-300 ${
                inView
                  ? `animate-fade-up animate-delay-${(i + 1) * 100}`
                  : "start-hidden"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-xl ${benefit.bg} border ${benefit.border} flex items-center justify-center ${benefit.color} mb-4`}
              >
                {benefit.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrizesSection() {
  const [ref, inView] = useInView();

  return (
    <section id="prizes" className="relative py-24 bg-bg-deep">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-brand-gold font-semibold tracking-wide uppercase text-sm mb-3">
            The Prizes
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Real rewards for{" "}
            <span className="text-brand-gold">real effort</span>
          </h2>
        </div>

        <div
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* Grand Prize */}
          <div
            className={`relative rounded-3xl p-[1px] bg-gradient-to-br from-brand-gold via-brand-gold/60 to-brand-gold/30 ${
              inView ? "animate-fade-up animate-delay-100" : "start-hidden"
            }`}
          >
            <div className="bg-bg-secondary rounded-3xl p-8 text-center h-full">
              <p className="text-brand-gold font-semibold tracking-wide uppercase text-xs mb-4">
                Grand Prize Drawing
              </p>
              <p className="text-5xl sm:text-6xl font-bold text-white mb-3">
                {PROGRAM.grandPrizeAmount}
              </p>
              <p className="text-text-secondary text-base mb-4">
                {PROGRAM.grandPrizeDescription}
              </p>
              <p className="text-text-tertiary text-sm">
                Drawing date: {PROGRAM.drawingDate}
              </p>
            </div>
          </div>

          {/* Weekly Prizes */}
          <div
            className={`relative rounded-3xl p-[1px] bg-gradient-to-br from-brand-teal via-brand-teal/60 to-brand-teal/30 ${
              inView ? "animate-fade-up animate-delay-200" : "start-hidden"
            }`}
          >
            <div className="bg-bg-secondary rounded-3xl p-8 text-center h-full">
              <p className="text-brand-teal font-semibold tracking-wide uppercase text-xs mb-4">
                Weekly Sim Competition
              </p>
              <p className="text-5xl sm:text-6xl font-bold text-white mb-3">
                {PROGRAM.weeklyPrizeAmount}
              </p>
              <p className="text-text-secondary text-base mb-4">
                {PROGRAM.weeklyPrizeDescription}
              </p>
              <p className="text-text-tertiary text-sm">
                New competition every week
              </p>
            </div>
          </div>
        </div>

        <p
          className={`text-center text-text-tertiary text-sm mt-8 ${
            inView ? "animate-fade-up animate-delay-300" : "start-hidden"
          }`}
        >
          Earn tickets by completing lessons, playing the Sim, and taking daily
          quizzes.{" "}
          <a
            href="#how-to-participate"
            className="text-brand-teal underline underline-offset-2 hover:text-white transition-colors"
          >
            See how to participate &darr;
          </a>
        </p>
      </div>
    </section>
  );
}

function ParticipateSection() {
  const [ref, inView] = useInView();

  return (
    <section id="how-to-participate" className="relative py-24 bg-bg-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-brand-teal font-semibold tracking-wide uppercase text-sm mb-3">
            How to Participate
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Four simple steps to{" "}
            <span className="text-brand-teal">start winning</span>
          </h2>
        </div>

        {/* Desktop: horizontal */}
        <div
          ref={ref}
          className="hidden lg:grid grid-cols-4 gap-6 items-start"
        >
          {steps.map((step, i) => (
            <div
              key={step.title}
              className={`relative flex flex-col items-center text-center ${
                inView
                  ? `animate-fade-up animate-delay-${(i + 1) * 100}`
                  : "start-hidden"
              }`}
            >
              {/* Connector */}
              {i < steps.length - 1 && (
                <div className="absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] flex items-center z-0">
                  <div className="flex-1 h-px bg-gradient-to-r from-border-primary to-border-primary/40" />
                  <svg
                    className="w-3 h-3 text-border-primary -ml-px flex-shrink-0"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path d="M2 1l8 5-8 5V1z" />
                  </svg>
                </div>
              )}

              <div
                className={`relative z-10 w-16 h-16 rounded-2xl ${step.bg}/15 border ${step.border} flex items-center justify-center mb-5`}
              >
                <span className={`text-xl font-bold ${step.color}`}>
                  {step.number}
                </span>
              </div>

              <h3 className={`text-xl font-bold mb-2 ${step.color}`}>
                {step.title}
              </h3>

              <p className="text-text-secondary text-sm leading-relaxed max-w-[220px]">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile: vertical */}
        <div className="lg:hidden flex flex-col gap-10">
          {steps.map((step, i) => (
            <div
              key={step.title}
              className="flex flex-col items-center text-center"
            >
              {i > 0 && (
                <div className="w-px h-8 bg-gradient-to-b from-border-primary/40 to-border-primary mb-4" />
              )}

              <div
                className={`w-14 h-14 rounded-2xl ${step.bg}/15 border ${step.border} flex items-center justify-center mb-4`}
              >
                <span className={`text-lg font-bold ${step.color}`}>
                  {step.number}
                </span>
              </div>

              <h3 className={`text-xl font-bold mb-2 ${step.color}`}>
                {step.title}
              </h3>

              <p className="text-text-secondary text-sm leading-relaxed max-w-xs">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Download CTA */}
        <div className="flex flex-wrap items-center gap-4 justify-center mt-14">
          <a
            href={PROGRAM.appStoreUrl}
            className="transition-transform hover:scale-105"
          >
            <img
              src="/images/appstore-badge.svg"
              alt="Download on the App Store"
              className="h-[48px]"
            />
          </a>
          <a
            href={PROGRAM.playStoreUrl}
            className="transition-transform hover:scale-105"
          >
            <img
              src="/images/googleplay-badge.svg"
              alt="Get it on Google Play"
              className="h-[48px]"
            />
          </a>
        </div>
      </div>
    </section>
  );
}

function EligibilitySection() {
  const [ref, inView] = useInView();

  const details = [
    { label: "Eligible", value: PROGRAM.eligibility },
    { label: "Minimum Age", value: PROGRAM.minAge },
    {
      label: "Program Dates",
      value: `${PROGRAM.programStart} - ${PROGRAM.programEnd}`,
    },
    { label: "Grand Prize Drawing", value: PROGRAM.drawingDate },
  ];

  return (
    <section id="eligibility" className="relative py-24 bg-bg-deep">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`text-center mb-12 ${
            inView ? "animate-fade-up" : "start-hidden"
          }`}
        >
          <p className="text-brand-indigo font-semibold tracking-wide uppercase text-sm mb-3">
            Eligibility &amp; Deadlines
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Who can{" "}
            <span className="text-brand-indigo">participate</span>
          </h2>
        </div>

        <div
          className={`bg-bg-secondary/60 border border-border-primary rounded-2xl p-8 ${
            inView ? "animate-fade-up animate-delay-100" : "start-hidden"
          }`}
        >
          <div className="space-y-5">
            {details.map((detail) => (
              <div key={detail.label}>
                <p className="text-white font-semibold text-sm mb-1">
                  {detail.label}
                </p>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {detail.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-border-primary">
            <a
              href={PROGRAM.officialRulesUrl}
              className="text-brand-indigo text-sm font-medium underline underline-offset-2 hover:text-white transition-colors"
            >
              View full Official Rules &rarr;
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const [ref, inView] = useInView();

  return (
    <section className="relative py-24 bg-bg-primary overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Copy - first on mobile */}
          <div
            className={`flex-1 min-w-0 order-1 lg:order-1 ${
              inView ? "animate-fade-up" : "start-hidden"
            }`}
          >
            <p className="text-brand-teal font-semibold tracking-wide uppercase text-sm mb-3">
              About Dough
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The finance app built for{" "}
              <span className="text-brand-teal">students</span>
            </h2>
            <p className="text-text-secondary text-lg mb-4">
              Dough is a personal finance app that combines short lessons, a
              7-day life simulation game, an AI coach, goal-setting tools, and
              competitions to make learning about money actually useful and fun.
            </p>
            <p className="text-text-secondary text-lg">
              No bank connections. No subscriptions. Just the tools you need to
              build real financial confidence - at your own pace.
            </p>
          </div>

          {/* Phone mockup */}
          <div
            ref={ref}
            className={`flex-shrink-0 order-2 lg:order-2 ${
              inView
                ? "animate-fade-right animate-delay-100"
                : "start-hidden"
            }`}
          >
            <PhoneMockup
              src="/images/screenshots/learn.png"
              alt="Dough learning modules"
              width={280}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [ref, inView] = useInView();

  return (
    <section id="faq" className="relative py-24 bg-bg-deep">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-brand-teal font-semibold tracking-wide uppercase text-sm mb-3">
            Support
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Frequently asked{" "}
            <span className="text-brand-teal">questions</span>
          </h2>
        </div>

        <div
          ref={ref}
          className={inView ? "animate-fade-up" : "start-hidden"}
        >
          <Accordion items={ustFaqs} />
        </div>
      </div>
    </section>
  );
}

function DownloadSection() {
  const [ref, inView] = useInView();

  return (
    <section id="download" className="relative py-24 bg-bg-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`relative rounded-3xl p-[1px] bg-gradient-to-br from-brand-teal via-brand-indigo to-brand-purple ${
            inView ? "animate-fade-up" : "start-hidden"
          }`}
        >
          <div className="bg-bg-secondary rounded-3xl px-8 py-16 sm:px-16 text-center relative overflow-hidden">
            <img
              src="/images/bots/waving.svg"
              alt=""
              className="absolute -bottom-4 -right-4 w-32 h-32 opacity-20 sm:opacity-40"
            />

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 relative z-10">
              Ready to grow your{" "}
              <span className="text-brand-teal">dough</span>?
            </h2>
            <p className="text-text-secondary text-lg mb-8 max-w-md mx-auto relative z-10">
              Download the app and enter your program code to get started.
            </p>

            <div className="flex flex-wrap items-center gap-4 justify-center relative z-10">
              <a
                href={PROGRAM.appStoreUrl}
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/appstore-badge.svg"
                  alt="Download on the App Store"
                  className="h-[52px]"
                />
              </a>
              <a
                href={PROGRAM.playStoreUrl}
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/googleplay-badge.svg"
                  alt="Get it on Google Play"
                  className="h-[52px]"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
